/**
 * Wallet service: USDC balance & transfers on Arc Testnet.
 *
 * USDC is the native currency on Arc Testnet (6 decimals), so balance is
 * fetched via eth_getBalance and transfers are plain native-value sends.
 */

import { ethers } from 'ethers';
import { ARC_TESTNET_NETWORK, USDC_DECIMALS } from '../config/blockchain';

const ensureArcNetwork = async () => {
  if (!window.ethereum) {
    throw new Error('MetaMask provider not available');
  }

  let provider = new ethers.BrowserProvider(window.ethereum);
  let network = await provider.getNetwork();

  if (Number(network.chainId) !== ARC_TESTNET_NETWORK.chainIdDecimal) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ARC_TESTNET_NETWORK.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: ARC_TESTNET_NETWORK.chainId,
            chainName: ARC_TESTNET_NETWORK.name,
            rpcUrls: [ARC_TESTNET_NETWORK.rpcUrl],
            blockExplorerUrls: [ARC_TESTNET_NETWORK.blockExplorer],
            nativeCurrency: ARC_TESTNET_NETWORK.nativeCurrency
          }],
        });
      } else {
        throw switchError;
      }
    }

    let attempts = 0;
    while (attempts < 10) {
      await new Promise(r => setTimeout(r, 300));
      provider = new ethers.BrowserProvider(window.ethereum);
      network = await provider.getNetwork();
      if (Number(network.chainId) === ARC_TESTNET_NETWORK.chainIdDecimal) break;
      attempts++;
    }

    if (Number(network.chainId) !== ARC_TESTNET_NETWORK.chainIdDecimal) {
      throw new Error(`Please switch to ${ARC_TESTNET_NETWORK.name} in MetaMask.`);
    }
  }

  return provider;
};

/**
 * Read connected wallet's USDC balance from the public Arc RPC.
 * Doesn't trigger a wallet prompt and doesn't require the user to be on the
 * Arc network in MetaMask.
 * @param {string} walletAddress
 * @returns {Promise<string>} balance formatted with 6 decimals
 */
export const getUSDCBalance = async (walletAddress) => {
  if (!walletAddress) return '0';
  const provider = new ethers.JsonRpcProvider(ARC_TESTNET_NETWORK.rpcUrl);
  const balance = await provider.getBalance(walletAddress);
  return ethers.formatUnits(balance, USDC_DECIMALS);
};

/**
 * Send USDC from the connected MetaMask wallet to a recipient address.
 * Will prompt to switch to Arc Testnet if needed.
 * @param {string} toAddress
 * @param {string|number} amount - human-readable amount (e.g. "1.5")
 * @returns {Promise<string>} transaction hash
 */
export const sendUSDC = async (toAddress, amount) => {
  if (!toAddress || !ethers.isAddress(toAddress)) {
    throw new Error('Invalid recipient address');
  }
  const parsed = String(amount).trim();
  if (!parsed || isNaN(Number(parsed)) || Number(parsed) <= 0) {
    throw new Error('Invalid amount');
  }

  const value = ethers.parseUnits(parsed, USDC_DECIMALS);

  const provider = await ensureArcNetwork();
  const signer = await provider.getSigner();
  const fromAddress = await signer.getAddress();

  const balance = await provider.getBalance(fromAddress);
  if (balance < value) {
    throw new Error('Insufficient USDC balance');
  }

  const tx = await signer.sendTransaction({ to: toAddress, value });
  await tx.wait();
  return tx.hash;
};
